import React from 'react'
import { motion, Variants } from 'framer-motion'

import Constants from '../../../app/utils/constants'

import featureImage from '../../../assets/images/cryptonary/feature_ring.svg'
import featureCoin from '../../../assets/images/cryptonary/feature_coin.webp'

import marketlogo from '../../../assets/images/cryptonary/marketlogo.webp'
import chartlogo from '../../../assets/images/cryptonary/chartlogo.webp'
import managementlogo from '../../../assets/images/cryptonary/managementlogo.webp'
import ai_insightlogo from '../../../assets/images/cryptonary/ai_insightlogo.webp'
import modulelogo from '../../../assets/images/cryptonary/modulelogo.webp'
import alertlogo from '../../../assets/images/cryptonary/alertlogo.webp'
import authenticationlogo from '../../../assets/images/cryptonary/authenticationlogo.webp'
import multiassettrackinglogo from '../../../assets/images/cryptonary/multiassettrackinglogo.webp'
import seamlessmobilelogo from '../../../assets/images/cryptonary/seamlessmobilelogo.webp'

import colors from '../../theme/color'

const featureIcons = [
  marketlogo,
  chartlogo,
  managementlogo,
  ai_insightlogo,
  modulelogo,
  alertlogo,
  authenticationlogo,
  multiassettrackinglogo,
  seamlessmobilelogo,
]

const FeaturesSection = () => {
  const { cryptonaryFeatureItems } = Constants

  const lineVariants: Variants = {
    hiddenLeft: {
      scaleX: 0,
      originX: 1,
    },

    hiddenRight: {
      scaleX: 0,
      originX: 0,
    },

    visible: {
      scaleX: 1,
      transition: {
        duration: 1.35,
        ease: [0.22, 1, 0.36, 1],
        delay: 0.26,
      },
    },
  }

  const featureCoinVariants: Variants = {
    initial: {
      y: 0,
    },

    animate: {
      y: [0, -10, 0],
      x: [0, -4, 0, 3, 0],
      rotateY: [0, 180, 360],
      rotateX: [0, 10, 0],
      scale: [1, 1.14, 1],
      rotate: [0, 2, 0],

      transition: {
        duration: 2.1,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  }

  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
    },

    visible: (index: number) => ({
      opacity: 1,
      y: 0,

      transition: {
        delay: index * 0.14,
        duration: 0.95,
        ease: 'easeOut',
      },
    }),
  }

  return (
    <section className="relative z-20 w-full overflow-hidden pb-24 md:pb-28 lg:pb-32">

      {/* Background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundColor:
            colors.cryptonarySectionBase,
        }}
      />

      <div
        className=" pointer-events-none absolute -left-44 top-[28%] hidden h-[640px] w-[500px] rounded-full opacity-[0.10] blur-3xl md:block "
        style={{
          backgroundColor:
            colors.cryptonaryGlowPrimary,
        }}
      />

      <div
        className=" pointer-events-none absolute -left-20 top-[44%] hidden h-[420px] w-[340px] rounded-full opacity-[0.30] blur-[120px] lg:block "
        style={{
          backgroundColor:
            colors.cryptonaryGlowSecondary,
        }}
      />

      <div className="relative z-10 w-full px-4 md:px-5 lg:px-6">

        {/* Heading */}
        <div className="relative mx-auto flex w-fit items-center gap-4 sm:gap-5">

          <motion.span
            variants={lineVariants}
            initial="hiddenLeft"
            whileInView="visible"
            viewport={{ once: false }}
            className="relative h-px w-[62px] sm:w-[78px]"
            style={{
              backgroundColor:
                `${colors.cryptonaryLineGreen}CC`,
            }}
          >
            <span
              className="
                absolute
                -left-[4px]
                top-1/2
                h-[6px]
                w-[6px]
                -translate-y-1/2
                rounded-full
              "
              style={{
                backgroundColor:
                  `${colors.cryptonaryLineMint}E6`,
              }}
            />
          </motion.span>

          <motion.h2
            initial={{
              opacity: 0,
              y: 10,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: false,
            }}
            transition={{
              duration: 0.95,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              font-gilroy-bold
              text-[2rem]
              leading-none
              sm:text-[2.2rem]
              md:text-[2.5rem]
            "
            style={{
              color:
                colors.cryptonaryAccentGreen,
            }}
          >
            Features
          </motion.h2>

          <motion.span
            variants={lineVariants}
            initial="hiddenRight"
            whileInView="visible"
            viewport={{
              once: false,
            }}
            className="relative h-px w-[62px] sm:w-[78px]"
            style={{
              backgroundColor:
                `${colors.cryptonaryLineGreen}CC`,
            }}
          >
            <span
              className=" absolute -right-[4px] top-1/2 h-[6px] w-[6px] -translate-y-1/2 rounded-full "
              style={{
                backgroundColor:
                  `${colors.cryptonaryLineMint}E6`,
              }}
            />
          </motion.span>

        </div>

        {/* Feature Image */}
        <div
          className=" relative mx-auto mt-10 w-full max-w-[760px] overflow-visible md:mt-14 min-[1800px]:max-w-[860px] min-[2200px]:max-w-[980px] "
        >

          <div
            className=" pointer-events-none absolute inset-x-[8%] top-[10%] z-0 h-[78%] rounded-full blur-[48px] "
            style={{
              backgroundImage: `
              radial-gradient(
              circle_at_50%_48%,
              ${colors.cryptonaryAccentGreen}4D 0%,
              ${colors.cryptonaryAccentGreen}29 34%,
              ${colors.cryptonaryAccentGreen}0D 60%,
              ${colors.cryptonaryAccentGreen}00 80%
            )`,
            }}
          />

          <img
            src={featureImage}
            alt="Feature Orbit"
            className=" not-[]:relative not-[]:z-10 not-[]:mx-auto not-[]:w-full not-[]:object-contain
              h-[350px]
              md:h-[450px]
              lg:h-[550px]
              xl:h-[650px]
              2xl:h-[800px]
            "
          />

        </div>

{/* FEATURES GRID */}
<div
className=" relative mx-auto mt-10 w-full max-w-[1240px] md:mt-12 min-[1800px]:max-w-[1460px] min-[2200px]:max-w-[1680px] "
>

{/* COIN */}
<motion.img
  variants={featureCoinVariants}
  initial="initial"
  animate="animate"
  src={featureCoin}
  alt="Feature coin"
  className=" pointer-events-none absolute z-30 object-contain left-0 top-0 -translate-x-[22%] -translate-y-[32%]
    w-[55px]
    sm:w-[70px]
    md:w-[85px]
    lg:w-[100px]
    xl:w-[120px]
    2xl:w-[145px]
    min-[2560px]:w-[180px]
  "
  style={{
    filter: `drop-shadow(0 16px 30px ${colors.cryptonaryFeatureCoinGlow}73)`,
    transformPerspective: 1200,
  }}
/>

<div
  className=" grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3 min-[1800px]:gap-x-8 min-[1800px]:gap-y-9 "
>

  {cryptonaryFeatureItems.map((item, index) => (
    <motion.div
      key={index}
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      whileHover="hover"
      className="group relative pt-9"
    >

      {/* ICON */}
      <div
        className=" absolute left-1/2 top-0 z-20 flex h-[60px] md:h-[72px] w-[60px] md:w-[72px] -translate-x-1/2 items-center justify-center rounded-full border shadow-[0_8px_24px_rgba(0,0,0,0.4)] "
        style={{
          borderColor: `${colors.cryptonaryFeatureIconRing}CC`,
          backgroundColor: colors.cryptonaryFeatureIconBg,
        }}
      >
        <motion.img
          src={featureIcons[index]}
          alt={`Feature icon ${index + 1}`}
          className="h-[24px] w-[24px] object-contain"
          variants={{
            hover: {
              y: [0, -8, 0],
              transition: {
                duration: 1,
                repeat: 4,
                ease: 'easeInOut',
              },
            },
          }}
        />
      </div>

      {/* CARD */}
      <motion.article
        className=" h-full rounded-[18px] px-2 pb-5 pt-11 sm:px-6 sm:pb-6 sm:pt-12 min-[1800px]:px-7 min-[1800px]:pb-7 min-[1800px]:pt-13 transition-colors duration-300
        "
        variants={{
          hover: {
            backgroundColor: `${colors.cryptonaryAccentGreen}4D`,
          },
        }}
        style={{
          backgroundColor: colors.cryptonaryFeatureCardBg,
        }}
      >
        <p
          className=" text-center font-gilroy-medium text-[0.8rem] leading-[1.34] sm:text-[1.12rem] min-[1800px]:text-[1.18rem] min-[2200px]:text-[1.25rem]
          "
          style={{
            color: colors.cryptonaryTextFeatureBody,
          }}
        >
          {item.text}
        </p>
      </motion.article>

    </motion.div>
  ))}

</div>
</div>

</div>
</section>
)
}

export default FeaturesSection